import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { business } from '../../data/business';
import { useOpenStatus } from '../../utils/openStatus';
import { PhoneIcon } from './icons';
import { CallLink, OrderLink } from './links';

// Same items, order and destinations as the site-wide header (layout/Header.tsx).
const LINKS = [
  { label: 'About', to: '#intro' },
  { label: 'Menu', to: '/menu' },
  { label: 'Locations', to: '#locations' },
  { label: 'Catering', to: '#catering' },
  { label: 'Gallery', to: '#social' },
  { label: 'Blog', to: '/blog' },
  { label: 'UW Students', to: '/near-uw' },
];

const NavItem = ({ label, to, onClick }: { label: string; to: string; onClick?: () => void }) =>
  to.startsWith('#') ? (
    <a href={to} onClick={onClick}>{label}</a>
  ) : (
    <Link to={to} onClick={onClick}>{label}</Link>
  );

/**
 * Fixed hairline nav. Transparent over the hero, bone once the hero has
 * scrolled away; Order Online is on screen at every width and scroll depth.
 */
const SpNav = () => {
  const [overHero, setOverHero] = useState(true);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const status = useOpenStatus();

  useEffect(() => {
    const hero = document.getElementById('hero');
    if (!hero || !('IntersectionObserver' in window)) {
      setOverHero(false);
      return;
    }
    const io = new IntersectionObserver(([entry]) => setOverHero(entry.isIntersecting), {
      rootMargin: '-80px 0px 0px 0px',
    });
    io.observe(hero);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <header className={`sp-nav${overHero && !open ? ' is-over' : ''}`}>
        <Link className="sp-brand" to="/" aria-label="Thaprachan Thai Cuisine, home">
          <span className="sp-logo">
            <img src="/assets/images/optimized/ThaprachanLogo.webp" alt="" width={52} height={52} />
          </span>
          <span className="sp-brand-name">Thaprachan</span>
        </Link>
        <nav className="sp-nav-links" aria-label="Primary">
          {LINKS.map((l) => (
            <NavItem key={l.label} {...l} />
          ))}
        </nav>
        <div className="sp-nav-right">
          <CallLink className="sp-btn sp-btn--sm sp-nav-reserve">Reservations</CallLink>
          <a className="sp-icon-btn" href={business.telephoneHref} data-track="call_click" aria-label={`Call ${business.telephoneDisplay}`}>
            <PhoneIcon />
          </a>
          <OrderLink className="sp-btn sp-btn--primary sp-btn--sm" />
          <button
            ref={triggerRef}
            type="button"
            className="sp-icon-btn"
            aria-expanded={open}
            aria-controls="sp-sheet"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((o) => !o)}
          >
            <span className="sp-burger" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className={`sp-sheet${open ? ' is-open' : ''}`} id="sp-sheet" aria-label="Site menu" aria-hidden={!open}>
        <nav aria-label="Mobile">
          {LINKS.map((l) => (
            <NavItem key={l.label} {...l} onClick={close} />
          ))}
        </nav>
        <div className="sp-sheet-foot">
          <p className={`sp-status is-${status.state}`}>
            <span className="sp-dot" aria-hidden="true" />
            <span>{status.headline}</span>
          </p>
          <CallLink className="sp-btn sp-btn--secondary">Reservations · {business.telephoneDisplay}</CallLink>
          <OrderLink />
        </div>
      </div>
    </>
  );
};

export default SpNav;
